import type { MouseEvent } from "react";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useRecordContext, type RaRecord } from "react-admin";
import { useWatch } from "react-hook-form";
import {
  formatItsDirectoryLabel,
  sabilShowHref,
  type FmbMatchedSabil,
  type FmbSabilMatch,
} from "./fmb-sabil-match";
import { useFmbSabilMatch } from "./useFmbSabilMatch";

function stopRowNav(event: MouseEvent) {
  event.stopPropagation();
}

function SabilNoLink({ sabil }: { sabil: FmbMatchedSabil }) {
  const no = sabil.sabilNo?.trim();
  if (!no) return <>—</>;
  return (
    <Link
      href={sabilShowHref(sabil.id)}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      variant="inherit"
      onClick={stopRowNav}
      onMouseDown={stopRowNav}
    >
      {no}
    </Link>
  );
}

/** Compact cell for matched sabils (list / show / forms). Sabil no. opens show in a new tab. */
export function FmbSabilMatchCell({ match }: { match: FmbSabilMatch | null | undefined }) {
  if (!match?.hasMatch || !match.sabils.length) {
    return <>No</>;
  }

  return (
    <Stack direction="column" spacing={0} sx={{ alignItems: "flex-start" }}>
      {match.sabils.map((sabil) => {
        const type = (sabil.sabilType || "Sabil").trim();
        const hofSuffix = sabil.matchedBy === "hof" ? " (HOF)" : "";
        return (
          <Typography component="span" variant="body2" key={sabil.id}>
            {type} <SabilNoLink sabil={sabil} />
            {hofSuffix}
          </Typography>
        );
      })}
    </Stack>
  );
}

/** Datagrid field: reads `sabilMatch` from the row record. */
export function FmbSabilMatchField(_props: { label?: string; sortable?: boolean }) {
  const record = useRecordContext<RaRecord>();
  return <FmbSabilMatchCell match={record?.sabilMatch as FmbSabilMatch | undefined} />;
}

/** Live hint under ITS on create/edit when the entered ITS is complete enough to look up. */
export function FmbSabilMatchFormHint() {
  const itsNo = useWatch({ name: "itsNo" });
  const { match, checking, inDirectory } = useFmbSabilMatch(itsNo);
  const its = itsNo == null ? "" : String(itsNo).trim();

  if (!its || its.length < 5 || !/^\d+$/.test(its)) {
    return null;
  }

  return (
    <Stack spacing={0.5} sx={{ mt: 0.5, mb: 1 }}>
      <Typography variant="body2" color="text.secondary">
        ITS in mohalla: {checking ? "…" : formatItsDirectoryLabel(inDirectory)}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        Sabil: {checking ? "…" : <FmbSabilMatchCell match={match} />}
      </Typography>
    </Stack>
  );
}

export function FmbItsDirectoryText({ inDirectory }: { inDirectory: boolean | null | undefined }) {
  return <>{formatItsDirectoryLabel(inDirectory)}</>;
}
